import { useEffect, useState } from "react";

import SignupModal from "@/components/signup-modal";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { INTERCOM_TOUR_STEPS } from "@/helpers/constants";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

import useSelectedCategories from "../../hooks/use-selected-categories";
import { IncludeEnum } from "../../types";
import { DropdownIcon } from "../icons";
import { AllIcon, NotPlayedIcon, PlayedIcon, WishlistIcon } from "./icons";

export const ICONS = {
  [IncludeEnum.ALL]: AllIcon,
  [IncludeEnum.NOT_PLAYED]: NotPlayedIcon,
  [IncludeEnum.PLAYED]: PlayedIcon,
  [IncludeEnum.SAVED]: WishlistIcon,
};

interface IncludeFilterProps {
  CustomMenuButton?: React.FC<{ children: string }>;
  onChange?(value: IncludeEnum): void;
  value: keyof typeof IncludeEnum;
}

export default function IncludeFilter({
  CustomMenuButton,
  onChange = () => {
    /* No-op */
  },
  value,
}: IncludeFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<keyof typeof IncludeEnum>(
    IncludeEnum.ALL
  );
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  const { isUser } = useAuth();
  const { includeLabels } = useSelectedCategories();

  const handleClick = (newValue: IncludeEnum) => () => {
    if (!isUser) {
      setIsSignupModalOpen(true);
      return;
    }

    setSelected(newValue);
    onChange(newValue);
    setIsOpen(false);
  };

  useEffect(() => {
    setSelected(value);
  }, [value]);

  return (
    <>
      <Popover onOpenChange={setIsOpen} open={isOpen}>
        {CustomMenuButton ? (
          <PopoverTrigger asChild>
            <CustomMenuButton>{includeLabels[selected]}</CustomMenuButton>
          </PopoverTrigger>
        ) : (
          <PopoverTrigger
            className={cn(
              INTERCOM_TOUR_STEPS.gamesButton,
              "group flex h-[31px] items-center rounded-[15px] border border-white/50 px-[11px] text-14 text-white hover:bg-white/10 md:h-7 md:px-3 md:font-medium md:text-12"
            )}
          >
            {includeLabels[selected]}
            <DropdownIcon className="ml-2 transition-transform group-has-data[state=open]:rotate-180" />
          </PopoverTrigger>
        )}
        <PopoverContent
          align="start"
          asChild
          className="flex w-auto min-w-56 flex-col rounded-[15px] bg-space-gray-dark shadow-0-0-8 shadow-black/25 md:gap-2.5 md:p-2.5"
        >
          <ul>
            {Object.entries(includeLabels).map(([key, value]) => {
              const Icon = ICONS[key as IncludeEnum];

              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: dis fine
                // biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy
                <li
                  className="flex h-10 cursor-pointer items-center gap-2.5 rounded-[10px] px-2.5 transition-colors hover:bg-white/10"
                  key={key}
                  onClick={handleClick(key as IncludeEnum)}
                >
                  <Icon className="inline" />
                  <span className="text-14 text-white">{value}</span>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
      <Input name="include" type="hidden" value={selected} />
      <SignupModal
        onOpenChange={setIsSignupModalOpen}
        open={isSignupModalOpen}
      />
    </>
  );
}
